import { Controller } from "@hotwired/stimulus"

// Date-range picker backed by the Cally <calendar-range> web component.
// Presets compute from/to relative to the current toDate (or today) and write them
// into the calendar; picking a range in the calendar updates the selection. Applying
// performs a full-page navigation to redirectPath?fromDate=&toDate= (merging
// additionalParams). The exact ?fromDate/?toDate query contract is preserved.
export default class extends Controller {
  static targets = ["calendar", "label", "rangeLabel", "presetButton"]
  static values = {
    fromDate: String,
    toDate: String,
    redirectPath: String,
    months: Number,
    additionalParams: Object,
  }

  connect() {
    // Seed internal selection from the server-provided values.
    this.from = this.fromDateValue
    this.to = this.toDateValue
  }

  // Cally emits a native `change` event whose target.value is "YYYY-MM-DD/YYYY-MM-DD"
  // (slash-joined range). Mirror it into the from/to selection and refresh the label.
  onCalendarChange(e) {
    const value = (e.target && e.target.value) || ""
    const [from, to] = value.split("/")
    if (from) this.from = from
    if (to) this.to = to
    else if (from) this.to = from
    this.updateRangeLabel()
  }

  // Reads data-preset (last3days/last7days/last30days/thisMonth/lastMonth), computes the
  // range, writes it into the calendar (slash-joined) and the from/to selection. No navigation.
  selectPreset(e) {
    const preset = e.currentTarget.dataset.preset
    const anchor = this.parse(this.to) || new Date()
    let from = new Date(anchor)
    let to = new Date(anchor)

    switch (preset) {
      case "last3days":
        from = this.addDays(to, -2)
        break
      case "last7days":
        from = this.addDays(to, -6)
        break
      case "last30days":
        from = this.addDays(to, -29)
        break
      case "thisMonth": {
        const now = new Date()
        from = new Date(now.getFullYear(), now.getMonth(), 1)
        to = new Date(now.getFullYear(), now.getMonth() + 1, 0)
        break
      }
      case "lastMonth": {
        const now = new Date()
        from = new Date(now.getFullYear(), now.getMonth() - 1, 1)
        to = new Date(now.getFullYear(), now.getMonth(), 0)
        break
      }
      default:
        return
    }

    this.from = this.format(from)
    this.to = this.format(to)

    if (this.hasCalendarTarget) {
      // Setting value re-renders the calendar selection and navigates it into view.
      this.calendarTarget.value = `${this.from}/${this.to}`
      if ("focusedDate" in this.calendarTarget) this.calendarTarget.focusedDate = this.from
    }
    this.updateRangeLabel()
  }

  // Full-page navigation to the new range, preserving additionalParams.
  apply() {
    const from = this.from || this.fromDateValue
    const to = this.to || this.toDateValue
    if (!from || !to) return

    const params = new URLSearchParams(this.additionalParamsValue || {})
    params.set("fromDate", from)
    params.set("toDate", to)

    const url = `${this.redirectPathValue}?${params.toString()}`
    if (typeof Turbo !== "undefined") {
      Turbo.visit(url)
    } else {
      window.location.assign(url)
    }
  }

  // Close the daisyUI dropdown by blurring the focused trigger/content.
  cancel() {
    if (document.activeElement) document.activeElement.blur()
    const el = this.element.querySelector("[tabindex]")
    if (el) el.blur()
  }

  // ---- helpers (no external libs) ----

  updateRangeLabel() {
    if (!this.hasRangeLabelTarget) return
    const from = this.parse(this.from)
    const to = this.parse(this.to)
    if (!from || !to) return
    this.rangeLabelTarget.textContent = `${this.formatLabel(from)} - ${this.formatLabel(to)}`
  }

  parse(str) {
    if (!str) return null
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str)
    if (!m) return null
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  }

  addDays(date, n) {
    const d = new Date(date)
    d.setDate(d.getDate() + n)
    return d
  }

  format(date) {
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, "0")
    const d = String(date.getDate()).padStart(2, "0")
    return `${y}-${m}-${d}`
  }

  formatLabel(date) {
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, "0")
    const d = String(date.getDate()).padStart(2, "0")
    return `${y}/${m}/${d}`
  }
}
